import type { AlgoliaProductType } from '@integration-layer/components/Algolia/AlgoliaAisInfiniteHits.props'
import type { RouteLocationNormalizedGeneric } from 'vue-router'
import type { PageHierarchyNode } from '@integration-layer/ampliance/schemas/page-hierarchy-node-schema.localized'
import { mapAmplienceSchemaToGAPageType } from '@integration-layer/utils/mapAmplienceSchemaToGAPageType'
import snakeCase from 'lodash/snakeCase'

// Extend the Window interface to include pageData
export type XgenPageData = {
  pageType: 'home' | 'subhome' | 'category' | 'pdp' | 'cart' | 'generic'
  macroCat: string | null
  microCat: string | null
  superMicroCat: string | null
  brand: string
  gender: 'man' | 'woman' | 'unisex'
  season?: string | null
  selectedFilters?: Record<string, string[]>
}

/** Extract PLP filters from query params. ONLY USE FOR XGEN PAGEDATA MIDDLEWARE*/
export const getPLPQueryFilters = (route: RouteLocationNormalizedGeneric) => {
  const queryFilters = (route.query?.filter as Object) ?? undefined

  if (!queryFilters) return

  // make sure the filters are correctly formatted
  const recordFilters = Object.entries(queryFilters).reduce(
    (result: Record<string, string[]>, [key, value]) => {
      if (
        (Array.isArray(value) || typeof value === 'string') &&
        value?.length
      ) {
        result[key] = Array.isArray(value) ? value : [value]
      }
      return result
    },
    {}
  )

  return recordFilters
}

/**
 * Finds the non-localized categoryId for the product from algolia Data
 * @param hierarchicalCategories - The object containing the hirarchy info for the product (localized)
 * @param categoryPageId2 - The array containing the category ids for all level of the hierarchy of the product (unlocalized but non specific)
 * @returns
 */
const getProductCategoriesEn = (
  hierarchicalCategories: AlgoliaProductType['hierarchicalCategories'],
  categoryPageId2: AlgoliaProductType['categoryPageId2']
): string[] => {
  // Get the depth of the category from hierarchicalCategories key names
  const hierarchyLevel = Object.keys(hierarchicalCategories)
    .map(key => parseInt(key.replace('lvl', '').replace('0', ''))) // Extract level number
    .sort()

  const deepestLevel = Math.max(...hierarchyLevel)

  // Find the deepest category page id (i.e. the most specific)
  const targetCategoryPageId = categoryPageId2?.find(categoryId => {
    const splitCategory = categoryId.split('>')
    return splitCategory.length === deepestLevel
  })

  if (!targetCategoryPageId) {
    return []
  }

  return targetCategoryPageId?.split('>').map(cat => cat.trim()) || []
}

/**
 * Extracts macro, micro, and super-micro categories from an unlocalized (i.e. in English) category list.
 * @param categoryList - An array of category strings in English.
 */
const getXgenDataFromCategoriesList = (
  categoryList: string[]
): {
  gender: 'man' | 'woman' | 'unisex'
  macroCat: string | null
  microCat: string | null
  superMicroCat: string | null
} => {
  const defaultData = {
    gender: 'unisex',
    macroCat: null,
    microCat: null,
    superMicroCat: null,
  } as const

  if (!categoryList.length) return defaultData

  const gender = categoryList.find(
    category => category === 'man' || category === 'woman'
  )

  // non-gendered pages, only have brand and categories
  if (!gender) {
    // some pages, like new-arrivals/kids, have different order of categories
    const [_brand, ...categories] = categoryList
    return {
      gender: 'unisex',
      macroCat: categories[0] || null,
      microCat: categories[1] || null,
      superMicroCat: categories[2] || categories[1] || null,
    }
  }

  // for gendered pages, always consider categories without the gender segment
  const [_brand, ...categoriesWithGender] = categoryList

  const categoriesWithoutGender = categoriesWithGender.filter(
    el => !['man', 'woman'].includes(el)
  )
  return {
    gender: gender ?? 'unisex',
    macroCat: categoriesWithoutGender[0] || null,
    microCat: categoriesWithoutGender[1] || null,
    superMicroCat:
      categoriesWithoutGender[2] || categoriesWithoutGender[1] || null,
  }
}

/**
 * Formats a website identifier into a GA-compatible brand name.
 */
const formatGABrand = (website: string) => {
  // Default brand if no specific website is selected
  let formattedBrand = 'armani_group'

  // Format brand based on current website
  switch (website) {
    case 'ea7':
      // Special case for EA7 brand
      formattedBrand = 'EA7'
      break
    case undefined:
    case 'global':
      // Default to Armani Group for global or undefined websites
      formattedBrand = 'armani_group'
      break

    default:
      // Convert other website values to snake_case
      formattedBrand = snakeCase(website)
      break
  }
  return formattedBrand
}

/**
 * Generates page hierarchy node payload key for data retrieval.
 */
const generatePageHierarchyNodePayloadKey = (
  to: RouteLocationNormalizedGeneric,
  currentCountry: string,
  currentLanguage: string
): string => {
  // Generate delivery key to retrieve page hierarchy data
  const deliveryKey = () => {
    const segments = to.path.split('/').slice(1)
    const [loc, country] = segments[0].split('-')
    segments[0] = `${loc?.toLowerCase()}-${country?.toUpperCase()}`
    return segments.join('/')
  }

  const vse = to.query.vse as string
  const locale =
    currentLanguage?.toLowerCase() + '-' + currentCountry?.toLowerCase()
  const payloadKey = `content__${deliveryKey()}__${locale}__${vse}`

  return payloadKey
}

/**
 * Extracts brand, page and category list information from a Amplience schema Hierarchy Page node.
 *
 * @param data
 * @returns
 */
const mapHierarchyNode = (data: PageHierarchyNode) => {
  // Extract brand from the first content item
  // @ts-ignore - Suppressing type checking for content access
  const brand = data.content[0].brand as string

  // Early return if schema is not available
  if (!data.schema) {
    return
  }

  // Extract the last part of the schema path
  const schemaArraySplitted = data.schema.split('/')
  const amplienceSchema = schemaArraySplitted[schemaArraySplitted.length - 1]
  const formattedPageType = mapAmplienceSchemaToGAPageType(
    amplienceSchema ?? ''
  )

  // @ts-ignore - Suppressing type checking for content access
  const categoryIdString = data.content[0]?.categoryId as string | undefined

  const categoryHierarchyList: string[] =
    categoryIdString?.split('>').map(el => el.trim()) ?? []

  return {
    pageType: formattedPageType,
    brand,
    categoryList: categoryHierarchyList,
  }
}

/**
 * This function hooks into the hierarchy page payload or the PDP fetch data to populate the Xgen data payload.
 */
export const buildXgenPageDataFromNuxtPayload = (): XgenPageData => {
  const route = useRoute()
  const { currentLanguage, currentCountry, website } = useAppConfig()

  const defaultState: XgenPageData = {
    pageType: 'generic',
    macroCat: null,
    microCat: null,
    superMicroCat: null,
    brand: formatGABrand(website),
    gender: 'unisex',
  }

  // Basic guards
  if (
    typeof currentLanguage !== 'string' ||
    typeof currentCountry !== 'string'
  ) {
    return { ...defaultState }
  }

  const nuxtApp = useNuxtApp()
  const payload = nuxtApp.payload

  const isProduct = payload['is_product'] as boolean
  const ampliencePageType = payload['amplience_page_type'] as string | null
  const isAmpliencePage = ampliencePageType !== null && ampliencePageType !== ''

  // PRODUCT PAGE LOGIC -------------------------------------------------------
  if (isProduct) {
    // Use product-state-for-ga (same source used in ProductPage.vue for GA events)
    const productState = useState<{
      algoliaProduct: AlgoliaProductType | null
    } | null>('product-state-for-ga')

    if (productState.value?.algoliaProduct) {
      const algoliaProduct = productState.value.algoliaProduct

      const categories = getProductCategoriesEn(
        algoliaProduct.hierarchicalCategories,
        algoliaProduct.categoryPageId2
      )

      const { gender, macroCat, microCat, superMicroCat } =
        getXgenDataFromCategoriesList(categories)

      return {
        ...defaultState,
        pageType: 'pdp',
        gender,
        macroCat,
        microCat,
        superMicroCat,
        season: algoliaProduct.salesSeason ?? null,
      }
    } else {
      return {
        ...defaultState,
        pageType: 'pdp',
        season: null,
      }
    }
  }

  // AMPLIENCE PAGE LOGIC -----------------------------------------------------
  if (isAmpliencePage) {
    // Retrieve hierarchy node via payload key (same as remapFallbackData + fetchPageXGenParams)
    const pageHierarchyPayloadKey = generatePageHierarchyNodePayloadKey(
      route,
      currentCountry,
      currentLanguage
    )

    const hierarchyNode = payload.data?.[pageHierarchyPayloadKey] as
      | PageHierarchyNode
      | undefined

    // If no page hierarchy data is found, set default state
    if (!hierarchyNode) return defaultState
    // Map page type from amplience schema name
    const mappedNode = mapHierarchyNode(hierarchyNode)
    if (!mappedNode) return defaultState
    const { pageType, categoryList: hierarchyPageCategories } = mappedNode

    // Lifestyle / subhome handling
    if (pageType === 'home' || pageType === 'subhome') {
      return {
        ...defaultState,
        pageType: pageType,
      }
    } else if (pageType === 'category' && hierarchyPageCategories?.length) {
      const { gender, macroCat, microCat, superMicroCat } =
        getXgenDataFromCategoriesList(hierarchyPageCategories)

      const queryFilters = getPLPQueryFilters(route)

      return {
        ...defaultState,
        pageType: 'category',
        gender,
        macroCat,
        microCat,
        superMicroCat,
        selectedFilters: queryFilters,
      }
    }
    return {
      ...defaultState,
    }
  }
  // GENERIC PAGE LOGIC -------------------------------------------------------
  const path = route.path
  const pathArray = removeLastEmptyParam(path.split('/')) as string[]
  const { pageType: routePageType } = mapRouteToGAPageType(pathArray)

  if (routePageType === 'cart') {
    return {
      ...defaultState,
      pageType: 'cart',
    }
  } else {
    return {
      ...defaultState,
    }
  }
}
